/**
 * Перенос `data/person_repository_impl.dart`.
 *
 * Riverpod в порте нет, поэтому `personRepositoryProvider` заменён на
 * [personRepository] — один общий экземпляр поверх общего `httpClient`.
 */

import { Endpoints } from '@/config/endpoints';
import type { PersonModel } from '@/domain/models/personModel';
import {
  personQuestionModelFromJson,
  type PersonQuestionModel,
} from '@/domain/models/personQuestionModel';
import type { PersonRepository } from '@/domain/repository/personRepository';
import {
  AppException,
  isHttpError,
  stackTraceOf,
  type HttpError,
} from '@/utils/exceptions/appException';
import { httpClient, type HttpClient } from '@/utils/http/httpClient';

/** Ответ на «подсказок у аватара нет» — это обычное состояние, не сбой. */
const HTTP_NOT_FOUND = 404;

/** Аргументы конструктора [PersonRepositoryImpl]. */
export interface PersonRepositoryImplInit {
  readonly client: HttpClient;
}

export class PersonRepositoryImpl implements PersonRepository {
  constructor({ client }: PersonRepositoryImplInit) {
    this.client = client;
  }

  readonly client: HttpClient;

  async fetchQuestions(personId: number): Promise<readonly PersonQuestionModel[]> {
    try {
      const response = await this.client.get(Endpoints.personQuestions(personId));
      // `response.data as List` в Dart бросает `TypeError`, когда тело не
      // список; слой исключений разбирает его так же.
      const items = response.data;
      if (!Array.isArray(items)) {
        throw new TypeError('person-questions: тело ответа не список');
      }
      return items.map((e: unknown) => personQuestionModelFromJson(e));
    } catch (e) {
      // По умолчанию подсказок для аватара нет, и бекенд отвечает 404.
      // Это обычное состояние, а не сбой: вопросов просто нет.
      //
      // В Dart для этого была отдельная ветка `on DioException`; здесь
      // ошибка транспорта опознаётся по форме — [isHttpError].
      if (isHttpError(e) && statusOf(e) === HTTP_NOT_FOUND) return [];
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async changePerson(person: PersonModel): Promise<void> {
    try {
      await this.client.post(Endpoints.personChange(person.personBaseUrl), {
        data: {
          person: person.slug,
        },
      });
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async sendInterrupt(person: PersonModel): Promise<void> {
    try {
      await this.client.post(Endpoints.personInterrupt(person.personBaseUrl));
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }
}

/**
 * `e.response?.statusCode` из Dart. Транспорт порта знает код и без
 * разобранного тела, поэтому `statusCode` идёт запасным источником — так же,
 * как в `AppException.handleHttpError`.
 */
function statusOf(error: HttpError): number | null {
  return error.response?.status ?? error.statusCode ?? null;
}

/** Аналог `personRepositoryProvider`: репозиторий на общем клиенте. */
export const personRepository: PersonRepository = new PersonRepositoryImpl({
  client: httpClient,
});
